import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLines } from "@/components/Reveal";

const CABINET_IMG =
  "https://images.unsplash.com/photo-1670003112050-710fbf757482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxnZW5ldmElMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnl8ZW58MHx8fHwxNzkyMjc1NjI5fDA&ixlib=rb-4.1.0&q=85";
const MEETING_IMG =
  "https://images.pexels.com/photos/8428082/pexels-photo-8428082.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Cabinet() {
  const { t } = useTranslation("cabinet");
  const principles = t("principles.items", { returnObjects: true });
  const people = t("governance.people", { returnObjects: true });
  const facts = t("legal.facts", { returnObjects: true });

  return (
    <div data-testid="cabinet-page" className="pt-32 lg:pt-40">
      <header className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <p className="overline-label mb-8 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-brass/60" />
          {t("hero.overline")}
        </p>
        <h1 className="max-w-3xl font-display text-4xl font-light leading-[1.08] tracking-tight text-chalk sm:text-5xl lg:text-6xl">
          <MaskedLines
            lines={[
              t("hero.titleA"),
              <span key="b" className="font-serif italic text-brass">{t("hero.titleB")}</span>,
            ]}
          />
        </h1>
      </header>

      <section className="mx-auto mt-16 max-w-7xl px-6 sm:px-8 lg:px-12 lg:mt-24">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="space-y-6 text-base leading-relaxed text-mist/85">
              <p>{t("history.para1")}</p>
              <p>{t("history.para2")}</p>
              <p>{t("history.para3")}</p>
            </div>
            <blockquote className="mt-12 border-l-2 border-brass pl-8 font-serif text-xl font-light italic leading-relaxed text-chalk sm:text-2xl">
              {t("history.quote")}
            </blockquote>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="space-y-4">
              <div className="overflow-hidden border border-white/10">
                <img
                  src={CABINET_IMG}
                  alt={t("history.imgAlt1")}
                  className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
              <div className="overflow-hidden border border-white/10">
                <img
                  src={MEETING_IMG}
                  alt={t("history.imgAlt2")}
                  className="h-56 w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate2/70">
                {t("history.caption")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal>
            <p className="overline-label">{t("purpose.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
              {t("purpose.titleA")}
              <span className="font-serif italic text-brass">{t("purpose.titleB")}</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-10 lg:grid-cols-2">
            <Reveal>
              <p className="text-sm leading-relaxed text-mist/85">{t("purpose.para1")}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm leading-relaxed text-slate2">{t("purpose.para2")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <Reveal>
          <p className="overline-label">{t("principles.overline")}</p>
          <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
            {t("principles.titleA")}
            <span className="font-serif italic text-brass">{t("principles.titleB")}</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06} className="bg-ink">
              <div data-testid={`about-principle-${i + 1}`} className="h-full p-8 transition-colors duration-300 hover:bg-elevated">
                <p className="font-mono text-xs tracking-[0.24em] text-brass/70">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-5 font-display text-lg text-chalk">{p.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate2">{p.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <Reveal>
          <p className="overline-label">{t("governance.overline")}</p>
          <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
            {t("governance.titleA")}
            <span className="font-serif italic text-brass">{t("governance.titleB")}</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          {people.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <article
                data-testid={p.testid}
                className="h-full border border-white/[0.08] bg-surface/40 p-8 transition-colors duration-300 hover:border-brass/35 lg:p-10"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-brass/70">
                  {p.role}
                </p>
                <h3 className="mt-4 font-display text-2xl text-chalk">{p.name}</h3>
                <p className="mt-5 text-sm leading-relaxed text-slate2">{p.bio}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <h2 className="font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
                {t("legal.overline")}
              </h2>
              <Link to="/contact" data-testid="cabinet-cta-contact" className="btn-brass mt-10">
                {t("legal.cta")} <ArrowUpRight size={15} />
              </Link>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-8">
              <dl data-testid="legal-facts-table" className="border-t border-white/[0.08]">
                {facts.map((f) => (
                  <div
                    key={f.label}
                    className="grid gap-1 border-b border-white/[0.08] py-5 sm:grid-cols-[240px_1fr] sm:gap-8"
                  >
                    <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate2">
                      {f.label}
                    </dt>
                    <dd className="text-sm text-chalk">{f.value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
